/**
 * Triggers a browser file download from a Blob.
 *
 * - Path separators (`/` and `\`) are stripped from `fileName` to prevent
 *   a server-supplied value from producing a deceptive download path.
 * - The blob URL is always revoked, even if the anchor click throws.
 */
export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const safeFileName = fileName.replace(/[/\\]/g, '_');
  const blobUrl = URL.createObjectURL(blob);
  try {
    Object.assign(document.createElement('a'), {
      href: blobUrl,
      download: safeFileName,
    }).click();
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Triggers a browser file download for a static or pre-signed URL.
 *
 * No fetch is performed; the browser navigates directly to the href.
 * - Only `http:`, `https:`, and relative URLs are allowed; any other scheme
 *   (e.g. `javascript:`, `data:`, `vbscript:`) throws to prevent DOM-XSS.
 * - Path separators (`/` and `\`) are stripped from `fileName` to prevent
 *   a caller-supplied value from producing a deceptive download path.
 */
export function triggerStaticDownload(href: string, fileName: string): void {
  const trimmedHref = href.trim();
  const schemeMatch = trimmedHref.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);

  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme !== 'http' && scheme !== 'https') {
      throw new Error(`Refusing to navigate to a non-http(s) URL: ${scheme}:`);
    }
  }

  const safeFileName = fileName.replace(/[/\\]/g, '_');

  Object.assign(document.createElement('a'), {
    href,
    download: safeFileName,
  }).click();
}

export interface FetchAndDownloadOptions {
  /** Fully-formed URL to GET. Encode path segments before passing. */
  url: string;
  /** Returns the bearer token; `undefined` means no Authorization header is sent. */
  getToken: () => Promise<string | undefined>;
  /** Filename for the saved file. Path separators are stripped automatically. */
  fileName: string;
  /**
   * Value for the `Accept` request header.
   * @default 'application/octet-stream'
   */
  accept?: string;
  /**
   * Substrings matched case-insensitively against the response `Content-Type`.
   * The download is rejected when none match.
   * Pass `[]` to disable validation.
   * @default ['octet-stream', 'spreadsheetml', 'openxmlformats']
   */
  allowedContentTypes?: string[];
}

/**
 * Fetches a URL with an optional Bearer token and triggers a file download.
 *
 * Throws on:
 * - Network failure
 * - Non-OK HTTP status
 * - Content-Type mismatch (when `allowedContentTypes` is non-empty)
 *
 * Callers are responsible for managing loading state and surfacing errors.
 */
export async function fetchAndDownload({
  url,
  getToken,
  fileName,
  accept = 'application/octet-stream',
  allowedContentTypes = ['octet-stream', 'spreadsheetml', 'openxmlformats'],
}: FetchAndDownloadOptions): Promise<void> {
  const token = await getToken();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: accept,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  if (allowedContentTypes.length > 0) {
    const contentType = response.headers.get('Content-Type') ?? '';
    if (!allowedContentTypes.some((t) => contentType.toLowerCase().includes(t.toLowerCase()))) {
      throw new Error(`Unexpected content type: ${contentType}`);
    }
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, fileName);
}
